import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdmin = userProfile?.isAdmin === true;

  const guestNavItems = useMemo(
    () => [
      { label: 'Home', path: '/', icon: 'Home' },
      { label: 'How It Works', path: '/how-it-works', icon: 'Info' },
      { label: 'Safety', path: '/safety-trust-center', icon: 'Shield' },
      { label: 'Sign In', path: '/registration-login', icon: 'LogIn' },
    ],
    []
  );

  const authenticatedNavItems = useMemo(() => {
    const items = [
      { label: 'Home', path: '/', icon: 'Home' },
      { label: 'How It Works', path: '/how-it-works', icon: 'Info' },
      { label: 'Safety', path: '/safety-trust-center', icon: 'Shield' },
      { label: 'Discover', path: '/search-discovery', icon: 'Search' },
      { label: 'Messages', path: '/messaging-system', icon: 'MessageCircle' },
      { label: 'Dashboard', path: '/member-dashboard', icon: 'LayoutDashboard' },
      { label: 'Account', path: '/user-profile-settings', icon: 'User' },
    ];
    if (isAdmin) {
      items.push({
        label: 'Admin',
        path: '/admin-panel',
        icon: 'Settings',
      });
    }
    return items;
  }, [isAdmin]);

  const navigationItems = user ? authenticatedNavItems : guestNavItems;

  const handleNavigation = (path) => {
    if (path?.includes('#')) {
      const [route, hash] = path?.split('#');
      navigate(route);
      setTimeout(() => {
        const element = document?.getElementById(hash);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(path);
    }
    setMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path === '/') {
      return location?.pathname === '/' || location?.pathname === '/home-landing';
    }
    return location?.pathname === path;
  };

  const handleGetStarted = () => {
    navigate('/registration-login');
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/registration-login');
    } catch (e) {
      console.error('Sign out error:', e);
    } finally {
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-[100] w-full bg-card shadow-warm">
        <div className="mx-auto max-w-[1200px] px-4 lg:px-6">
          <div className="flex h-16 items-center gap-2 min-w-0">
            <div
              className="flex shrink-0 items-center gap-3 cursor-pointer"
              onClick={() => handleNavigation('/')}
            >
              <img
                src="/assets/images/Logo_NoBG-1769596999637.png"
                alt="Bamchik - Discreet Dating Platform"
                className="h-10 w-auto object-contain"
              />
              <h1 className="text-2xl font-heading font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent hidden sm:block">
                Bamchik
              </h1>
            </div>

            <div className="hidden md:flex flex-1 min-w-0 items-center justify-end gap-2">
              <nav
                className="flex min-w-0 flex-1 items-center justify-end gap-0.5 overflow-x-auto overscroll-x-contain whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                aria-label="Main navigation"
              >
                {navigationItems?.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => handleNavigation(item.path)}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm transition-smooth lg:gap-2 lg:px-3 ${
                      isActivePath(item.path)
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted hover:text-primary'
                    }`}
                  >
                    <Icon
                      name={item.icon}
                      size={20}
                      color={isActivePath(item.path) ? 'var(--color-primary)' : 'currentColor'}
                    />
                    <span className="font-caption font-medium">{item.label}</span>
                  </button>
                ))}
              </nav>
              {user && (
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm font-medium transition-smooth hover:bg-muted/80"
                >
                  <Icon name="LogOut" size={18} />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              )}
              {!user && (
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2.5 text-sm font-medium text-white transition-smooth hover:shadow-warm-lg hover:scale-[1.02] lg:px-6"
                >
                  <Icon name="Sparkles" size={20} color="#FFFFFF" />
                  Get Started
                </button>
              )}
            </div>

            <button
              type="button"
              className="shrink-0 p-2 rounded-lg hover:bg-muted transition-smooth md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[200] md:hidden">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute inset-x-0 top-16 bg-card shadow-warm-lg animate-slide-down max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg transition-smooth ${
                    isActivePath(item.path)
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  <Icon
                    name={item.icon}
                    size={24}
                    color={isActivePath(item.path) ? 'var(--color-primary)' : 'currentColor'}
                  />
                  <span className="font-caption font-medium text-base">{item.label}</span>
                </button>
              ))}
              {user && (
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full flex items-center justify-center gap-2 p-4 rounded-lg bg-muted hover:bg-muted/80 transition-smooth mt-2"
                >
                  <Icon name="LogOut" size={22} />
                  Sign Out
                </button>
              )}
              {!user && (
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="w-full flex items-center justify-center gap-2 p-4 rounded-lg bg-gradient-to-r from-primary to-secondary text-white font-medium transition-smooth hover:shadow-warm-lg mt-4"
                >
                  <Icon name="Sparkles" size={24} color="#FFFFFF" />
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicHeader;
